import {
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  Image,
  Progress,
  Text,
  useDisclosure,
  Spinner,
} from '@chakra-ui/react';
import { useMemo, useState, useCallback } from 'react';
import { Trans } from '@lingui/react/macro';
import { ImagePlus } from 'lucide-react';
import { UserAvatar } from '@duke-hq/ui/components/UserAvatar/UserAvatar';
import type { RouterOutputs } from '@duke-hq/svc-api/provider';
import { FormModalWrapper } from '@duke-hq/ui/components/Modals/FormModalWrapper';
import { AllocatePointsModal } from './AllocatePointsModal';

type CauseCardProps = {
  cause: RouterOutputs['groupRewards']['listCauses'][number];
  onContribution?: () => void;
};

export function CauseCard({ cause }: CauseCardProps) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [imageLoading, setImageLoading] = useState(true);

  const handleImageLoad = useCallback(() => {
    setImageLoading(false);
  }, []);

  const handleImageError = useCallback(() => {
    setImageLoading(false);
  }, []);

  const totalContributed = useMemo(() => {
    if (!('contributions' in cause) || !Array.isArray(cause.contributions)) {
      return 0;
    }

    return cause.contributions.reduce(
      (
        sum: number,
        contribution: {
          pointsAllocated: number | null | undefined;
        },
      ) => {
        const points = contribution?.pointsAllocated;
        return sum + (typeof points === 'number' ? points : 0);
      },
      0,
    );
  }, [cause]);

  const targetAmount = typeof cause.targetAmount === 'number' ? cause.targetAmount : 0;
  const formattedRaised = `$${totalContributed.toLocaleString()} raised`;
  const remaining = Math.max(0, targetAmount - totalContributed);

  const percentage = useMemo(() => {
    return targetAmount > 0 ? (totalContributed / targetAmount) * 100 : 0;
  }, [targetAmount, totalContributed]);

  const formattedRemaining = `$${remaining.toLocaleString()} remain.`;

  return (
    <Box
      w="100%"
      h="100%"
      bg="white"
      borderRadius="lg"
      overflow="hidden"
      p={4}
      display="flex"
      flexDirection="column"
    >
      <Flex
        w="100%"
        aspectRatio={3 / 2}
        borderRadius="md"
        bg="bg.muted"
        align="center"
        justify="center"
        flexShrink={0}
        color="white"
        position="relative"
      >
        {cause.imageUrl != null ? (
          <>
            {imageLoading === true && (
              <Spinner
                size="md"
                color="fg.muted"
                position="absolute"
                top="50%"
                left="50%"
                transform="translate(-50%, -50%)"
                zIndex={1}
              />
            )}
            <Image
              src={cause.imageUrl}
              alt={cause.name}
              w="100%"
              h="100%"
              objectFit="cover"
              borderRadius="md"
              onLoad={handleImageLoad}
              onError={handleImageError}
              style={{
                opacity: imageLoading ? 0 : 1,
                transition: 'opacity 0.3s ease-in-out',
              }}
            />
          </>
        ) : (
          <Icon as={ImagePlus} boxSize={6} color="fg.muted" />
        )}
      </Flex>

      <Flex direction="column" py={2} gap={3} flex="1">
        <Text fontSize="sm" fontWeight="bold">
          {cause.name}
        </Text>

        <HStack spacing={1} alignItems="center">
          <Text fontSize="xs" color="fg.muted">
            <Trans>Suggested by</Trans>{' '}
          </Text>
          <UserAvatar
            user={{
              first_name:
                'suggestedBy' in cause && cause.suggestedBy?.first_name
                  ? cause.suggestedBy.first_name
                  : '',
              last_name:
                'suggestedBy' in cause && cause.suggestedBy?.last_name != null
                  ? cause.suggestedBy.last_name
                  : '',
              picture_url:
                'suggestedBy' in cause && cause.suggestedBy?.picture_url != null
                  ? cause.suggestedBy.picture_url
                  : null,
            }}
            width="16px"
            height="16px"
            boxSize="4"
            fontSize="2xs"
          />
          <Text fontSize="xs" fontWeight="semibold">
            {'suggestedBy' in cause && cause.suggestedBy != null
              ? [cause.suggestedBy.first_name ?? '', cause.suggestedBy.last_name ?? '']
                  .filter(Boolean)
                  .join(' ')
              : ''}
          </Text>
        </HStack>

        <Text fontSize="xs" color="fg.muted" noOfLines={3}>
          {cause.description != null ? cause.description : ''}
        </Text>

        <Box>
          <Text fontSize="md" fontWeight="bold">
            {formattedRaised}
          </Text>
          <Progress size="sm" value={percentage} mt={2} borderRadius="full" />
          <Text fontSize="xs" color="fg.muted" mt={1}>
            <Text as="span" fontWeight="bold">
              {Math.round(percentage)}%
            </Text>{' '}
            <Trans>of target reached.</Trans> {formattedRemaining}
          </Text>
        </Box>

        <Box mt="auto">
          <Button variant="outline" size="md" w="100%" mt={2} onClick={onOpen}>
            <Trans>Contribute</Trans>
          </Button>
        </Box>

        <FormModalWrapper
          modalContent={<AllocatePointsModal cause={cause} onClose={onClose} />}
          isOpen={isOpen}
          onClose={onClose}
          onOpen={onOpen}
          modalContentHeight="auto"
        />
      </Flex>
    </Box>
  );
}
